import React from "react";
import { useContentContext } from "@/components/content-chat/ContentContext";
import { Card } from "@/components/ui/card";
import { DocumentSummarySkeleton, MediaSummarySkeleton } from "@/components/content-chat/skeletons";

const DOCUMENT_TYPES = ["pdf", "doc", "ppt", "web", "text"];
const MEDIA_TYPES = ["audio", "video", "youtube", "recording"];

interface SummaryIntroScreenProps {
  contentId?: string | null;
  isLoading?: boolean;
}

export const SummaryIntroScreen: React.FC<SummaryIntroScreenProps> = ({
  contentId,
  isLoading = false,
}) => {
  const { content } = useContentContext();
  const isDocument = content && DOCUMENT_TYPES.includes(content.type);
  const isMedia = content && MEDIA_TYPES.includes(content.type);

  const getSourceText = () => {
    if (isDocument) return "from your document";
    if (isMedia) return "from the transcript";
    return "from your content";
  };

  const renderSkeleton = () => {
    if (isDocument) return <DocumentSummarySkeleton />;
    if (isMedia) return <MediaSummarySkeleton />;
    return <DocumentSummarySkeleton />;
  };

  return (
    <div className="h-full overflow-y-auto px-4 sm:px-0">
      <div className="mx-auto mb-10 w-full max-w-3xl">
        <div className="space-y-6 pt-4">
          {/* Intro Message */}
          <div className="py-5 text-center">
            <p className="text-muted-foreground">
              {isLoading
                ? "Generating summary..."
                : "Ask Shattara AI to generate a summary to see it here."}
            </p>
          </div>

          {/* Summary */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-primary/80">Summary</h3>
            {isLoading ? (
              <Card className="rounded-2xl border border-primary/10 bg-card p-4">
                {renderSkeleton()}
              </Card>
            ) : (
              <Card className="rounded-2xl border border-primary/10 bg-card p-4 text-sm text-muted-foreground">
                No summary yet. Ask Shattara AI to generate a summary{" "}
                <span className="font-semibold text-foreground">{getSourceText()}</span>.
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
